import { Component, ElementRef, HostListener, Injector, OnDestroy, TemplateRef, Type, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChatSheet, ChatSheetService } from '../../chat-sheet.service';
import { Observable, Subscription, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { ChatService } from '../../chat.service';
import { ChatUiService, ChatWindowState } from '../../chat-ui.service';
import { ChatPushService } from '../../chat-push.service';
import { ChatSoundService } from '../../chat-sound.service';
import { ConversationSummary, ConversationType, MemberView } from '../../core/hub-types';
import { ConversationTransition, conversationTransition } from '../../ui/conversation-transition';
import { ConversationTone, conversationTone, headerSubtitle } from '../../ui/conversation-tone';
import { avatarHue, initialsOf } from '../../core/avatar';
import { HubStatus } from '../../core/hub-connection';
import { unreadBadge } from '../../ui/unread-badge';
import { ChatWindowBox, ChatWindowSize, ResizeEdge, clampSize, defaultSize, resizeWindow, shownAboveKeyboard, showsTwoPanes, PHONE_PX } from '../../ui/chat-window-size';
import { VisibleArea, visibleArea } from '../../../shared/directives/visible-area';
import { ConversationActionsService } from '../../conversation-actions.service';

/**
 * The floating chat window (as in MedicAI Connect): one for the whole app, mounted in the dashboard shell so it stays
 * open across pages. Bottom-right, draggable by its header, resizable from any edge or corner, minimise to the header,
 * close.
 *
 * When the window is wide enough (TWO_PANE_MIN_WIDTH) the conversation list sits to the left of the open
 * conversation, as a desktop chat app does; narrower than that it is one pane at a time with a back arrow, and full
 * screen on a phone.
 */

/** The conversation pane's entrance for each kind of change: from the right, from the left, or fading in. */
const KEYFRAMES: Record<Exclude<ConversationTransition, 'none'>, Keyframe[]> = {
  // Opaque a little before it lands, so the slide reads as movement rather than a fade.
  forward: [{ transform: 'translateX(48px)', opacity: 0 }, { opacity: 1, offset: 0.7 }, { transform: 'none', opacity: 1 }],
  back: [{ transform: 'translateX(-48px)', opacity: 0 }, { opacity: 1, offset: 0.7 }, { transform: 'none', opacity: 1 }],
  fade: [{ opacity: 0 }, { opacity: 1 }],
};
/** Long enough to be seen, short enough not to hold anyone up; a gentle ease-out. */
const TIMING: Record<Exclude<ConversationTransition, 'none'>, KeyframeAnimationOptions> = {
  forward: { duration: 750, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  back: { duration: 750, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  fade: { duration: 550, easing: 'ease-out' },
};

/** Where the size the user picked is kept, per browser. */
const SIZE_KEY = 'chat.window.size';
/** Width of the window while minimised to its header. */
const MINIMIZED_WIDTH = 380;
/** Gap between the window and the screen edge when it sits in its default corner. */
const EDGE = 24;

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
})
export class ChatWindowComponent implements OnDestroy {
  readonly vm$: Observable<{
    ui: ChatWindowState;
    conversation: ConversationSummary | null;
    title: string;
    status: HubStatus;
    enabled: boolean | null;
    isOrgAdmin: boolean;
    /** Shown on the bar while minimised: the same count as the top bar and sidebar chat badges. */
    badge: string;
  }>;
  /** The window's size: the user's own if they resized it, otherwise the default for this screen. */
  size: ChatWindowSize = defaultSize(window.innerWidth, window.innerHeight);
  /** Distance of the window's bottom-right corner from the screen's (null: the default corner). */
  position: { right: number; bottom: number } | null = null;
  private userSized = false;
  private drag: { dx: number; dy: number; width: number; height: number; moved: boolean } | null = null;
  resize: { x: number; y: number; edge: ResizeEdge; start: ChatWindowBox } | null = null;
  /** The edges and corners that resize the window. */
  readonly edges: ResizeEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
  private justDragged = false;
  /** Who is in the open conversation (null until fetched), and whether the slide-down list of them is showing. */
  members: MemberView[] | null = null;
  /** The panel open inside the window (new conversation, details, ...), and what it is given. */
  sheet: ChatSheet | null = null;
  sheetInjector: Injector | null = null;
  /** The conversation's colour, for its menu (which renders outside the window, so cannot inherit it). */
  menuAccent = '#009688';
  /** The conversation pane, animated when the open conversation changes. */
  @ViewChild('stage') stage?: ElementRef<HTMLElement>;
  /** Where the window was before the latest change: the list, or a kind of conversation. */
  private place: ConversationType | 'list' = 'list';
  membersOpen = false;
  private membersFor: string | null = null;
  private readonly sub = new Subscription();
  /** The part of the screen showing: above the soft keyboard while it is up. */
  visible: VisibleArea = visibleArea();
  private readonly onViewport = (): void => {
    this.visible = visibleArea();
  };

  constructor(
    readonly chat: ChatService,
    readonly ui: ChatUiService,
    readonly sheets: ChatSheetService,
    private readonly injector: Injector,
    push: ChatPushService,
    sound: ChatSoundService,
    private readonly actions: ConversationActionsService,
  ) {
    // The window lives in the app shell: the right place to pick up notification clicks and ?openChat links (M4).
    push.init();
    sound.init();
    window.visualViewport?.addEventListener('resize', this.onViewport);
    window.visualViewport?.addEventListener('scroll', this.onViewport);
    const saved = readSavedSize();
    if (saved) {
      this.size = clampSize(saved, window.innerWidth, window.innerHeight);
      this.userSized = true;
    }
    this.vm$ = combineLatest([this.ui.state$, this.chat.store.state$, this.chat.status$, this.chat.enabled$, this.chat.orgId$, this.chat.unread$]).pipe(
      map(([ui, state, status, enabled, orgId, unread]) => {
        const conversation = ui.conversationId ? state.conversations[ui.conversationId] ?? null : null;
        const me = state.me;
        return {
          ui,
          conversation,
          title: conversation ? this.chat.store.titleOf(conversation) : 'Chat',
          status,
          enabled,
          isOrgAdmin: !!me && !!orgId && me.orgs.some((o) => o.orgId === orgId && o.role === 'admin'),
          badge: unreadBadge(unread),
        };
      }),
    );
    // Switching practice while one of the other practice's conversations is open: back to the list.
    this.sub.add(
      combineLatest([this.chat.orgId$, this.chat.store.state$, this.ui.state$]).subscribe(([orgId, state, ui]) => {
        const c = ui.conversationId ? state.conversations[ui.conversationId] : null;
        if (c && orgId && c.orgId !== orgId) this.ui.showList();
      }),
    );
    // The panel showing inside the window, with an injector that gives it its data and its ref (as a dialog's).
    this.sub.add(
      this.sheets.sheet$.subscribe((sheet) => {
        this.sheet = sheet;
        this.sheetInjector = sheet
          ? Injector.create({
              providers: [{ provide: MAT_DIALOG_DATA, useValue: sheet.data }, { provide: MatDialogRef, useValue: sheet.ref }],
              parent: this.injector,
            })
          : null;
      }),
    );
    // A panel belongs to the open window: closing the window closes it.
    this.sub.add(this.ui.state$.subscribe((ui) => { if (!ui.open) this.sheets.closeAll(); }));
    // A different conversation: forget the last one's members and fetch this one's for the line under its name.
    this.sub.add(
      this.ui.state$
        .pipe(map((ui) => ui.conversationId), distinctUntilChanged())
        .subscribe((id) => {
          this.members = null;
          this.membersOpen = false;
          this.membersFor = id;
          if (id) void this.loadMembers(id);
          this.animateTo(id);
        }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    window.visualViewport?.removeEventListener('resize', this.onViewport);
    window.visualViewport?.removeEventListener('scroll', this.onViewport);
  }

  tone(conversation: ConversationSummary | null): ConversationTone {
    return conversationTone(conversation?.type);
  }

  subtitle(conversation: ConversationSummary): string {
    return headerSubtitle(conversation.type, this.members, this.chat.store.snapshot.me?.principalId, conversation.access === 'participant');
  }

  // ── notifications and muting, from the header's menu ──────────────────────────────

  /** A group's photo, for the header in place of its icon. */
  photoOf(conversation: ConversationSummary | null): string | null {
    return conversation ? this.chat.store.photoUrlOf(conversation) : null;
  }

  /** The patient file this conversation is about, if this practice has one (patient, referral, patient-app chat). */
  fileOf(conversation: ConversationSummary | null): string | null {
    return this.actions.fileOf(conversation);
  }

  openFile(patientRef: string): void {
    this.actions.openFile(patientRef);
  }

  toneOf(win: HTMLElement): string {
    return getComputedStyle(win).getPropertyValue('--w-tone').trim() || '#009688';
  }

  muted(conversation: ConversationSummary): boolean {
    return this.chat.store.isMuted(conversation);
  }

  /** The name in the header slides the list of who is here down over the messages, and back up. */
  toggleMembers(conversation: ConversationSummary, e: Event): void {
    e.stopPropagation();
    if (this.justDragged || this.ui.snapshot.minimized) return;
    this.membersOpen = !this.membersOpen;
    // Fetched again on opening, so someone added since the conversation opened is there.
    if (this.membersOpen) void this.loadMembers(conversation.id);
  }

  isMe(m: MemberView): boolean {
    return m.principalId === this.chat.store.snapshot.me?.principalId;
  }

  facePhoto(m: MemberView): string | null {
    return this.chat.store.avatarUrl(m.principalId, m.avatar ?? null);
  }

  faceInitials(m: MemberView): string {
    return initialsOf(m.name);
  }

  faceHue(m: MemberView): number {
    return avatarHue(m.principalId);
  }

  private async loadMembers(id: string): Promise<void> {
    try {
      const members = await this.chat.store.members(id);
      // The conversation may have changed while this was in flight.
      if (this.membersFor === id) this.members = members;
    } catch {
      // No list is shown rather than an error: the header still names the conversation.
    }
  }

  headerClick(): void {
    if (this.justDragged) return;
    if (this.ui.snapshot.minimized) this.ui.setMinimized(false);
  }

  info(conversation: ConversationSummary): void {
    this.actions.info(conversation);
  }

  get phone(): boolean {
    return window.innerWidth <= PHONE_PX;
  }

  /** The conversation list beside the open conversation, when the window has room for both. */
  get twoPane(): boolean {
    return !this.phone && showsTwoPanes(this.size.width);
  }

  get right(): number {
    return this.position?.right ?? EDGE;
  }

  /** Kept low enough that a window restored from its header still fits below the top of the screen. */
  get bottom(): number {
    const bottom = this.position?.bottom ?? EDGE;
    return this.ui.snapshot.minimized ? bottom : Math.min(bottom, Math.max(0, window.innerHeight - this.size.height));
  }

  /** Where the open window is drawn: its own place, raised above a soft keyboard that would cover its composer. */
  get shown(): { height: number; bottom: number } {
    return shownAboveKeyboard({ height: this.size.height, bottom: this.bottom }, window.innerHeight, this.visible);
  }

  /** How much of the foot of the screen the soft keyboard covers; a phone's full-screen window ends above it. */
  get keyboardCover(): number {
    return Math.max(0, window.innerHeight - this.visible.bottom);
  }

  get width(): number {
    return this.ui.snapshot.minimized ? Math.min(this.size.width, MINIMIZED_WIDTH) : this.size.width;
  }

  // ── dragging by the header (desktop only) ───────────────────────────────────────

  startDrag(e: PointerEvent, el: HTMLElement): void {
    if (e.button !== 0 || this.phone || (e.target as HTMLElement).closest('button:not(.who)')) return;
    const rect = el.getBoundingClientRect();
    this.drag = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, width: rect.width, height: rect.height, moved: false };
    this.justDragged = false;
  }

  // ── resizing from any edge or corner (desktop only) ─────────────────────────────

  startResize(e: PointerEvent, edge: ResizeEdge): void {
    if (e.button !== 0 || this.phone) return;
    e.preventDefault();
    e.stopPropagation();
    this.resize = { x: e.clientX, y: e.clientY, edge, start: { ...this.size, right: this.right, bottom: this.bottom } };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(e: PointerEvent): void {
    if (this.resize) {
      const { edge, start } = this.resize;
      const box = resizeWindow(start, edge, e.clientX - this.resize.x, e.clientY - this.resize.y, window.innerWidth, window.innerHeight);
      this.size = { width: box.width, height: box.height };
      if (box.right !== start.right || box.bottom !== start.bottom) this.position = { right: box.right, bottom: box.bottom };
      return;
    }
    if (!this.drag) return;
    this.drag.moved = true;
    const left = Math.min(Math.max(0, e.clientX - this.drag.dx), window.innerWidth - this.drag.width);
    const top = Math.min(Math.max(0, e.clientY - this.drag.dy), window.innerHeight - 48);
    this.position = {
      right: Math.max(0, window.innerWidth - left - this.drag.width),
      bottom: Math.max(0, window.innerHeight - top - this.drag.height),
    };
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    if (this.resize) {
      this.resize = null;
      this.userSized = true;
      saveSize(this.size);
      return;
    }
    if (!this.drag) return;
    this.justDragged = this.drag.moved;
    this.drag = null;
    // The click that ends a drag must not also restore/minimise.
    setTimeout(() => (this.justDragged = false));
  }

  /**
   * The page change, as a direction: deeper (into a patient's conversation, or from the list) slides in from the
   * right, back up from the left, and the same depth crossfades (conversation-transition.ts). Only while the window is
   * showing, and not for people who ask their system for less motion.
   */
  private animateTo(id: string | null): void {
    const to: ConversationType | 'list' = id ? this.chat.store.snapshot.conversations[id]?.type ?? 'channel' : 'list';
    const kind = conversationTransition(this.place, to);
    this.place = to;
    const ui = this.ui.snapshot;
    if (kind === 'none' || !ui.open || ui.minimized) return;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Started now, before the new conversation renders, so it never shows for a frame at full strength first (the jump).
    this.stage?.nativeElement.animate?.(KEYFRAMES[kind], { ...TIMING[kind], fill: 'backwards' });
  }

  sheetTemplate(sheet: ChatSheet): TemplateRef<unknown> | null {
    return sheet.content instanceof TemplateRef ? sheet.content : null;
  }

  sheetComponent(sheet: ChatSheet): Type<unknown> | null {
    return sheet.content instanceof TemplateRef ? null : sheet.content;
  }

  /** Escape answers an open question "no" first, then closes the panel, as it would a dialog. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.sheets.currentConfirm) this.sheets.answer(false);
    else this.sheet?.ref.close();
  }

  @HostListener('window:resize')
  onResize(): void {
    // A size the user picked is kept as far as the screen allows; otherwise the default for the new screen.
    this.size = this.userSized
      ? clampSize(this.size, window.innerWidth, window.innerHeight)
      : defaultSize(window.innerWidth, window.innerHeight);
    // Keep a dragged window on screen after the viewport shrinks.
    if (this.position) {
      this.position = {
        right: Math.min(this.position.right, Math.max(0, window.innerWidth - this.size.width)),
        bottom: Math.min(this.position.bottom, Math.max(0, window.innerHeight - 48)),
      };
    }
  }
}

function readSavedSize(): ChatWindowSize | null {
  try {
    const saved = JSON.parse(localStorage.getItem(SIZE_KEY) ?? 'null');
    return saved && typeof saved.width === 'number' && typeof saved.height === 'number' ? saved : null;
  } catch {
    return null;
  }
}

function saveSize(size: ChatWindowSize): void {
  try {
    localStorage.setItem(SIZE_KEY, JSON.stringify(size));
  } catch {
    // Private windows and blocked storage: the size simply is not remembered.
  }
}
